<template>
  <div class="fill-height">
    <v-card class="rounded-xl card-border-red h-100 pa-4">
      <v-skeleton-loader v-if="loading" class="mx-auto" type="card" />
      <v-row v-else class="fill-height align-center justify-center">
        <v-col cols="12" sm="12" md="10" lg="9" class="text-center">
          <p class="card-p">
            <orders-schedules
              v-if="orderStats && orderStats.pedidosMes"
              :orders="orderStats.pedidosMes"
              :title="'Mes'"
            />
            <orders-schedules
              v-if="orderStats && orderStats.pedidosMes"
              :orders="orderStats.pedidoSemana"
              :title="'Semana'"
            />
            <b class="text-subtitle-2 font-weight-bold">
              Zona ({{ data?.zoneId }})</b
            >
          </p>
          <p class="card-p">
            {{ data?.zoneFullDesc }}
          </p>
        </v-col>
        <v-col cols="12" sm="2" class="text-left">
          <traffic-light
            :seller="{
              PERNR: data.user.id,
              VTWEG: data.user.channel,
            }"
          />
        </v-col>
        <v-col cols="12" class="text-center pb-0 pt-0">
          <span class="card-p font-weight-medium"
            >({{ data?.user?.id }}) {{ data?.user?.name }}
            <data-update-step-launcher v-if="$hasAnyRole(['Seller'])" />
          </span>
        </v-col>
        <v-col cols="12" md="5" lg="6" class="text-left">
          <change-password-control v-if="userCanUpdatePassword" class="mb-4" />
          <span class="card-p mb-3 text-subtitle-2"><b>Antigüedad:</b></span>
          <p class="card-p text-body-2 mb-0">IUSA: {{ data?.seniority }}</p>
        </v-col>
        <v-col
          cols="12"
          sm="7"
          md="6"
          class="text-left text-sm-center mt-n5 mt-md-n2"
        >
          <div class="d-flex flex-row justify-center align-center">
            <sales-policy-file-downloader
              class="mx-1"
              icon-size="25"
              policy-type="car"
              :icon-class="
                data?.hasCar > 0 ? 'car-policy-enabled' : 'car-policy-disabled'
              "
            />
            <v-tooltip bottom class="mt-2">
              <template #activator="{ on, attrs }">
                <a @click="goTo('PlaneadorSeller')">
                  <v-icon color="#0069D9" v-bind="attrs" alt="" v-on="on"
                    >mdi-calendar-check-outline</v-icon
                  >
                </a>
              </template>
              <span>Programación del vendedor</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <a
                  href="https://iusaenlinea.com/multimedia-video"
                  target="_blank"
                >
                  <img
                    class="ml-1 mr-1"
                    v-bind="attrs"
                    src="/img/icon-metro-video-camera.svg"
                    width="21px"
                    alt=""
                    v-on="on"
                  />
                </a>
              </template>
              <span>Videoteca</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <a href="#" @click.prevent="handleFirstLinkClick">
                  <svg-icon
                    v-bind="attrs"
                    size="35"
                    name="iusa-capacita"
                    label="IUSA CAPACITA"
                    v-on="on"
                  />
                </a>
              </template>
              <span>IUSA Capacita</span>
            </v-tooltip>
          </div>
          <v-row class="mt-1">
            <v-col cols="6" sm="6" lg="6" class="pa-0">
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <v-icon v-bind="attrs" color="green" v-on="on">
                    mdi-account-supervisor-circle
                  </v-icon>
                </template>
                <span>Población</span>
              </v-tooltip>
              <p v-if="!editHab" class="text-caption" @click="evalFactAnt">
                {{ formatFloat(citizens.total ?? 0, 0) }} H.
              </p>
              <div v-else>
                <div class="text-left text-caption">Editar</div>
                <div class="d-flex align-items-center">
                  <v-text-field
                    v-model="citizens.total"
                    :disabled="citizens.loading"
                    :loading="citizens.loading"
                    hide-details="auto"
                    solo
                    style="width: 100%; min-width: 90px"
                    @input="validateCitizens(citizens.total)"
                  />
                  <v-btn
                    color="primary"
                    icon
                    class="ml-1"
                    :disabled="citizens.loading"
                    :loading="citizens.loading"
                    @click="updateSellerDetails"
                  >
                    <v-icon>mdi mdi-send-circle</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-col>
            <v-col cols="6" sm="5" lg="6" class="pa-0">
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <v-icon v-bind="attrs" color="blue" v-on="on">
                    mdi-account-multiple-check
                  </v-icon>
                </template>
                <span>Venta por habitante</span>
              </v-tooltip>
              <p class="text-caption">{{ calcBillingPopulation }}$/H</p>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <alert-prominent v-if="errorMessage" :message="errorMessage" />
    </v-card>
  </div>
</template>

<script>
  /* eslint-disable vue/require-default-prop, vue/require-prop-types */
  import { mapGetters } from 'vuex';
  import TrafficLight from '@/components/TrafficLight.vue';
  import axios from '@/plugins/axios';
  import moment from 'moment';
  import OrdersSchedules from '../components/OrdersSchedules.vue';
  import ChangePasswordControl from '@/components/ChangePasswordControl.vue';
  import DataUpdateStepLauncher from '@/template/partials/stepsAfterLogin/DataUpdateStepLauncher.vue';
  import AlertProminent from '@/components/AlertProminent.vue';
  import numbersFormats from '@/mixins/numbersFormats';
  import SvgIcon from '@/components/icons/SvgIcon.vue';
  import SalesPolicyFileDownloader from '@/components/policies/SalesPolicyFileDownload.vue';

  export default {
    name: 'SellerInfo',
    components: {
      TrafficLight,
      OrdersSchedules,
      ChangePasswordControl,
      DataUpdateStepLauncher,
      AlertProminent,
      SvgIcon,
      SalesPolicyFileDownloader,
    },
    mixins: [numbersFormats],
    props: {
      data: {
        type: Object,
        default: () => ({
          user: {
            id: '',
            channel: '',
          },
          billingLastMonth: 0,
          hasCar: 0,
          zoneId: '',
          zoneFullDesc: '',
          name: '',
          seniority: '',
        }),
      },
      loading: {
        type: Boolean,
      },
      errorMessage: {
        type: String,
        default: '',
      },
    },
    data() {
      return {
        editHab: false,
        idSellerDetails: '',
        numbsOfCitizen: 0,
        trafficlight: 0,
        loadSellerDetails: true,
        orderStats: ['pedidoSemana', 'pedidosMes'],
        citizens: {
          total: 0,
          zone: '',
          loading: false,
        },
      };
    },
    computed: {
      ...mapGetters({
        authenticated: 'auth/authenticated',
        user: 'auth/user',
      }),
      userCanUpdatePassword() {
        return this.$hasAnyRole(['Seller']);
      },
      calcBillingPopulation() {
        if (this.data.billingLastMonth > 0 && this.citizens.total > 0) {
          return this.formatNumberOptionalDecimals(
            this.data.billingLastMonth / this.citizens.total,
            '',
            0
          );
        }
        return 0;
      },
    },
    watch: {
      data: {
        handler() {
          this.getOrderStats();
          this.getCitizens();
        },
        deep: true,
      },
    },
    methods: {
      async getCitizens() {
        try {
          const response = await axios.get(
            `/citizens/zone/${this.data.zoneId}`
          );
          this.citizens.total = response.data;
          this.citizens.zone = this.data.zoneId;
        } catch (error) {
          this.$swal({
            title: 'Ha ocurrido un error al cargar los ciudadanos',
            text: error.message || 'Error desconocido',
            icon: 'error',
          });
        }
      },
      goTo(routeName) {
        this.$router.push({ name: routeName });
      },
      async getOrderStats() {
        try {
          let response = await axios.get(
            '/seller/home/planner-orders-schedule',
            {
              params: {
                employee_number: this.data.user.id,
                inicio: moment().startOf('month').format('YYYY-MM-DD'),
                fin: moment().endOf('month').format('YYYY-MM-DD'),
                P_VKORG: this.user.VKORG,
                P_VTWEG: this.data.user.channel,
              },
              noCancelRequest: true,
              cachable: true,
            }
          );
          this.orderStats = response.data.data;
        } catch (error) {
          this.$swal({
            title: 'Ha currido un error al cargar el planeador',
            icon: 'error',
          });
        }
      },
      async updateSellerDetails() {
        try {
          this.citizens.loading = true;
          await axios.put('/citizens/update', {
            citizens: this.citizens.total ?? 0,
            zone: this.citizens.zone,
          });
          this.editHab = false;
          this.citizens.loading = false;
        } catch (error) {
          this.citizens.total = this.data.citizens;
          this.citizens.loading = false;
          this.$toast.error(
            'Hubo problemas al actualizar el número de habitantes, por favor intente más tarde'
          );
          this.editHab = false;
        }
      },
      evalFactAnt() {
        this.editHab = true;
      },
      handleFirstLinkClick() {
        window.open(
          `https://capacita.iusa.com.mx/acceso.valida.php?u=${this.data.user.id}`,
          '_blank'
        );
      },
      validateCitizens(citizens) {
        this.$nextTick(() => {
          this.citizens.total = citizens.replace(/\D/g, '');
        });
      },
    },
  };
</script>
